import useLocalStorage from "@rehooks/local-storage";
import { useMemo } from "react";

import Box from "ds/components/Box";
import Callout from "ds/components/Callout";
import Link from "ds/components/Link";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import { AnalyticsPage } from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import useCanConfirmRun from "shared/Run/access/useCanConfirmRun";
import useCanDiscardRun from "shared/Run/access/useCanDiscardRun";
import useCanPromoteRun from "shared/Run/access/useCanPromoteRun";
import useCanRetryRun from "shared/Run/access/useCanRetryRun";
import useCanReviewRun from "shared/Run/access/useCanReviewRun";
import useCanSkipExternalDependency from "shared/Run/access/useCanSkipExternalDependency";
import useCanStopRun from "shared/Run/access/useCanStopRun";
import useCanPerformTaskAgain from "shared/Stack/access/useCanPerformTaskAgain";
import { RunState, RunType } from "types/generated";
import { isModuleRunType } from "utils/run";
import { AccountContext } from "views/AccountWrapper";
import useCanPrioritizeRun from "shared/Run/access/useCanPrioritizeRun";
import { RunContext } from "components/Run/Context";

import BlockerInfo from "../../elements/BlockerInfo";
import CommentForm from "../../elements/CommentForm";
import ExpiredInfo from "../../elements/ExpiredInfo";
import HistoryTimeline from "../../elements/HistoryTimeline";
import NavigationBar from "../../elements/NavigationBar";
import NegativeRunInfo from "../../elements/NegativeRunInfo";
import RunPageWrapper from "../../elements/RunPageWrapper";
import StackLockNotice from "../../elements/StackLockNotice";
import WorkerPoolEntry from "../../entries/WorkerPoolEntry";
import { RunElementsProvider } from "../../RunElementsContext";
import { RunEntryContext } from "../../types";
import { createRunTimeline } from "../../utils/createRunTimeline";

type RunHistoryProps = {
  analyticsPage?: AnalyticsPage | undefined;
};

const RunHistory = ({ analyticsPage }: RunHistoryProps) => {
  const { run, stack, versionId } = useTypedContext(RunContext);
  const { viewer } = useTypedContext(AccountContext);
  const isModuleRun = isModuleRunType(run.type);
  const isTaskRun = run.type === RunType.Task;
  const isModuleVersionRun = isModuleRun && !!versionId;
  const isProposedRun = run.type === RunType.Proposed;
  const [isSimpleView, setIsSimpleView] = useLocalStorage("simple-view", true);

  const isStack = stack.__typename === "Stack";

  const isRunBlocking =
    run.state === RunState.Pending ||
    run.state === RunState.Queued ||
    run.state === RunState.Ready ||
    run.state === RunState.Confirmed;

  const isRunProposed = run.type === RunType.Proposed;

  const canPerformAgainValueFn = useCanPerformTaskAgain();
  const canPerformAgainValue = isStack ? canPerformAgainValueFn(run, stack) : undefined;
  const canConfirmFn = useCanConfirmRun();
  const canConfirm = isStack ? canConfirmFn(run, stack) : undefined;

  const canDiscard = useCanDiscardRun()(run, stack);
  const canSkipExternalDependencies = useCanSkipExternalDependency()(stack);

  const canPrioritize = useCanPrioritizeRun()({
    space: stack.spaceDetails,
    canWrite: stack.canWrite,
    hasWorkerPool: !!stack.workerPool,
    isRunFinished: run.finished,
  });

  const canPromote = useCanPromoteRun()(run, stack);

  const canStop = useCanStopRun()(run, stack);

  const canReview = useCanReviewRun()(run, stack);

  const canRetry = useCanRetryRun()(run, stack);

  const shouldShowBlockerInfo = isStack && !isRunProposed && isRunBlocking;

  const shouldShowExpiredInfo = isStack && run.expired;
  const runHooks = useMemo(
    () => ({
      afterRun: run.runtimeConfig?.afterRun,
      afterApply: run.runtimeConfig?.afterApply,
      afterDestroy: run.runtimeConfig?.afterDestroy,
      afterInit: run.runtimeConfig?.afterInit,
      afterPerform: run.runtimeConfig?.afterPerform,
      afterPlan: run.runtimeConfig?.afterPlan,
      beforeApply: run.runtimeConfig?.beforeApply,
      beforeDestroy: run.runtimeConfig?.beforeDestroy,
      beforeInit: run.runtimeConfig?.beforeInit,
      beforePerform: run.runtimeConfig?.beforePerform,
      beforePlan: run.runtimeConfig?.beforePlan,
    }),
    [run.runtimeConfig]
  );

  let runQueryToRefetch = "GetRun";

  if (isModuleVersionRun) {
    runQueryToRefetch = "GetVersionRun";
  } else if (isModuleRun) {
    runQueryToRefetch = "GetModuleRun";
  }

  const entryContext = useMemo<RunEntryContext>(
    () => ({
      canReview,
      runHooks,
      isSimpleView: isSimpleView!,
      canConfirm,
      canDiscard,
      canPerformAgain: canPerformAgainValue,
      runId: run.id,
      stackId: stack.id,
      runTaskCommand: run.command,
      isStackDisabled: stack.isDisabled,
      isModuleRun,
      canRetry,
      runRetryBlocker: run.retryBlocker,
      runState: run.state,
      canStop,
      isRunFinished: run.finished,
      isTaskRun,
      canPromote,
      isProposedRun,
      runQueryToRefetch,
      analyticsPage,
      canSkipExternalDependencies,
    }),
    [
      canReview,
      canRetry,
      runHooks,
      isSimpleView,
      canConfirm,
      canDiscard,
      canPerformAgainValue,
      run.id,
      stack.id,
      run.command,
      stack.isDisabled,
      isModuleRun,
      run.retryBlocker,
      run.state,
      canStop,
      run.finished,
      isTaskRun,
      canPromote,
      isProposedRun,
      runQueryToRefetch,
      analyticsPage,
      canSkipExternalDependencies,
    ]
  );

  const historyList = useMemo(
    () =>
      createRunTimeline(
        {
          history: run.history,
          policyReceipts: run.policyReceipts,
          reviews: run.reviews,
          comments: run.comments,
          planPoliciesOutcomes: run.planPoliciesOutcomes,
          runDependencies: run.dependsOn,
          externalDependencies: run.externalDependencies,
        },
        entryContext
      ),
    [
      entryContext,
      run.history,
      run.comments,
      run.reviews,
      run.policyReceipts,
      run.dependsOn,
      run.externalDependencies,
      run.planPoliciesOutcomes,
    ]
  );

  return (
    <Box direction="column" grow="1" fullWidth style={{ position: "relative" }} id="runWrapper">
      <PageInfoHeader title="Run history">
        <Toggle variant="switch" isSelected={isSimpleView!} onChange={setIsSimpleView}>
          Simplified view
        </Toggle>
      </PageInfoHeader>
      <RunElementsProvider>
        {shouldShowExpiredInfo && <ExpiredInfo stack={stack} />}
        {shouldShowBlockerInfo && <BlockerInfo stack={stack} run={run} />}
        {run.pluginLogs && run.pluginLogs.length > 0 && (
          <Callout variant="info">
            <Typography tag="p" variant="p-body3" color="secondary">
              You have some output logs from your plugins. Check them out{" "}
              <Link href="./plugin-outputs" inheritFont>
                here
              </Link>
            </Typography>
          </Callout>
        )}
        {isStack && stack.lockedBy !== null && (
          <StackLockNotice lockedAt={stack.lockedAt} lockedBy={stack.lockedBy} viewer={viewer.id} />
        )}
        {run.expectFailure && <NegativeRunInfo />}
        <RunPageWrapper>
          <CommentForm runId={run.id} stack={stack} runQueryToRefetch={runQueryToRefetch} />

          <HistoryTimeline>
            {isRunBlocking && (
              <WorkerPoolEntry
                workerPool={stack.workerPool}
                canPrioritize={canPrioritize}
                isRunPrioritized={run.isPrioritized}
                runId={run.id}
                stackId={stack.id}
                runQueryToRefetch={runQueryToRefetch}
              />
            )}
            {historyList}
          </HistoryTimeline>
        </RunPageWrapper>

        <NavigationBar next={run?.next?.id} previous={run?.previous?.id} />
      </RunElementsProvider>
    </Box>
  );
};

export default RunHistory;
